import { isFiniteNumber } from "@/utils/numberHelpers"

export type SfValue = number[] | null
export type SfInput = number | number[] | null | undefined

export type FormatSfDisplayOptions = {
  sfIteration?: boolean
}

/** Dedupe finite SF values while preserving first-seen order. */
export const normalizeSf = (value: SfInput): SfValue => {
  if (value == null) {
    return null
  }

  if (Array.isArray(value)) {
    const seen = new Set<number>()
    const normalized: number[] = []
    for (const item of value) {
      if (!isFiniteNumber(item) || seen.has(item)) continue
      seen.add(item)
      normalized.push(item)
    }
    return normalized.length > 0 ? normalized : null
  }

  return isFiniteNumber(value) ? [value] : null
}

const DEFAULT_SF_DISPLAY_MAX = 4

/** Format SF for UI; when iteration is off, show only the primary (first) SF. */
export const formatSfDisplay = (
  sf: SfInput,
  max: number = DEFAULT_SF_DISPLAY_MAX,
  options?: FormatSfDisplayOptions,
): string => {
  if (options?.sfIteration === false) {
    const primary = getPrimarySf(sf)
    return primary != null ? String(primary) : ""
  }

  const normalized = normalizeSf(sf)
  if (!normalized?.length) {
    return ""
  }

  if (normalized.length <= max) {
    return normalized.join(",")
  }

  return `${normalized.slice(0, max).join(",")}...`
}

export const isEmptySf = (sf: SfInput): boolean => !normalizeSf(sf)?.length

export const getPrimarySf = (sf: SfInput): number | null =>
  normalizeSf(sf)?.[0] ?? null

export const coerceSfForIteration = (
  sf: SfInput,
  sfIteration: boolean,
): SfValue => {
  if (sfIteration) {
    return normalizeSf(sf)
  }

  const primary = getPrimarySf(sf)
  return primary != null ? [primary] : null
}

export const toggleSfValue = (sf: SfInput, value: number): SfValue => {
  const normalized = normalizeSf(sf) ?? []
  const next = normalized.includes(value)
    ? normalized.filter((item) => item !== value)
    : [...normalized, value]

  return next.length > 0 ? next.sort((left, right) => left - right) : null
}

export const sfValuesEqual = (left: SfInput, right: SfInput): boolean => {
  const normalizedLeft = normalizeSf(left)
  const normalizedRight = normalizeSf(right)

  if (normalizedLeft == null && normalizedRight == null) {
    return true
  }

  if (normalizedLeft == null || normalizedRight == null) {
    return false
  }

  if (normalizedLeft.length !== normalizedRight.length) {
    return false
  }

  const sortedLeft = [...normalizedLeft].sort((a, b) => a - b)
  const sortedRight = [...normalizedRight].sort((a, b) => a - b)

  return sortedLeft.every((value, index) => value === sortedRight[index])
}
